import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { doc, getDoc, runTransaction, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDb } from '@talk2nick/shared';
import {
  createDefaultUserSettings,
  isOwnerEmail,
  isReservedForOwner,
  normalizeHandle,
  resolveLocale,
  validateHandle,
  type HandleError,
  type PinSetting,
} from '@talk2nick/core';
import { generateSalt, hashPin } from '@/pin';

/** 사용 가능 여부 확인 디바운스 — 타이핑 중 매 글자마다 읽지 않도록 */
const AVAILABILITY_DEBOUNCE_MS = 400;
/** 제출 시 선점 transaction 재시도 (PLAN 5-1장 4항) */
const CLAIM_RETRIES = 3;
const CLAIM_BACKOFF_MS = 300;

/** 유니크 ID가 이미 선점된 경우 — 제출 시점 경합에서만 발생 */
export class HandleTakenError extends Error {
  constructor() {
    super('handle-taken');
    this.name = 'HandleTakenError';
  }
}

export type HandleCheckStatus = 'idle' | 'invalid' | 'checking' | 'available' | 'taken' | 'error';

export interface HandleAvailability {
  status: HandleCheckStatus;
  reason: HandleError | null;
  /** 소문자 정규화된 값 — 제출·확인에 공통으로 쓴다 */
  normalized: string;
}

/**
 * HandleError → i18n 키.
 * `HANDLE_ERROR_MESSAGES`는 한국어 고정 문자열이라 다국어 UI에서는 쓰지 않는다.
 */
export function handleErrorKey(reason: HandleError): string {
  switch (reason) {
    case 'too_short':
      return 'onboarding.errTooShort';
    case 'too_long':
      return 'onboarding.errTooLong';
    case 'invalid_chars':
      return 'onboarding.errInvalidChars';
    case 'leading_trailing_dot':
    case 'consecutive_dots':
      return 'onboarding.errDot';
    case 'reserved':
      return 'onboarding.errReserved';
    case 'owner_only':
      return 'onboarding.errOwnerOnly';
    case 'numeric_only':
      return 'onboarding.errNumericOnly';
    case 'empty':
      return 'onboarding.invalid';
  }
}

/**
 * 입력한 ID의 규칙 검증 + 사용 가능 여부 확인.
 *
 * 확인 시점의 "사용 가능"은 예약이 아니다 — 최종 판정은 제출 시
 * `handles/{id}` transaction이 한다. 그래서 이 훅은 UX 힌트만 제공한다.
 */
export function useHandleAvailability(input: string, email?: string | null): HandleAvailability {
  const normalized = normalizeHandle(input);
  const [state, setState] = useState<{
    status: HandleCheckStatus;
    reason: HandleError | null;
  }>({ status: 'idle', reason: null });

  useEffect(() => {
    const validation = validateHandle(input);
    if (!validation.ok) {
      setState({
        status: validation.reason === 'empty' ? 'idle' : 'invalid',
        reason: validation.reason,
      });
      return;
    }

    // 소유자 전용 ID를 다른 계정이 고른 경우 — 서버에 도달하기 전에 막는다.
    // 서버 측도 firestore.rules 에서 같은 조건으로 막지만, 여기서 먼저 멈춰야
    // "이미 사용 중"이라는 사실과 다르게 보이는 혼선을 만들지 않는다.
    if (isReservedForOwner(validation.normalized) && !isOwnerEmail(email)) {
      setState({ status: 'invalid', reason: 'owner_only' });
      return;
    }

    let cancelled = false;
    setState({ status: 'checking', reason: null });

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const snap = await getDoc(doc(getDb(), 'handles', validation.normalized));
          if (cancelled) return;
          setState({ status: snap.exists() ? 'taken' : 'available', reason: null });
        } catch (error) {
          console.error('[talk2nIcK] ID 확인 실패:', error);
          if (!cancelled) setState({ status: 'error', reason: null });
        }
      })();
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [input, email]);

  return { ...state, normalized };
}

/**
 * 온보딩 최종 제출 (PLAN 5-1장).
 *
 * 쓰기 순서는 Rules가 강제한다: `users/{uid}.handle`이 `handles/{handle}.uid`와
 * 일치해야 하므로 반드시 handles를 먼저 선점한 뒤 users를 만든다.
 *  1) `handles/{id}` transaction 선점 (재시도 멱등)
 *  2) PBKDF2-SHA256 100k로 PIN 해시
 *  3) `users/{uid}` 생성 (기본 settings + PIN 포함)
 */
export async function submitOnboarding(user: User, rawHandle: string, pin: string): Promise<void> {
  const validation = validateHandle(rawHandle);
  if (!validation.ok) throw new Error(`invalid-handle:${validation.reason}`);
  const handle = validation.normalized;

  // 전용 ID 강제는 제출 경로에서도 다시 검사한다. 가용성 훅은 타이핑 중
  // UX 힌트일 뿐이라, 폼을 조작해 통과하는 경로를 막는 것이 이 곳이다.
  if (isReservedForOwner(handle) && !isOwnerEmail(user.email)) {
    throw new Error('invalid-handle:owner_only');
  }

  await claimHandle(user.uid, handle);

  // PIN 해시는 네트워크 왕복 전에 CPU 작업으로 끝낸다
  const salt = generateSalt();
  const hash = await hashPin(pin, salt);
  const pinSetting: PinSetting = { hash, salt, updatedAt: Date.now() };

  // 기본 대화명으로 사용자가 설정한 ID(handle) 값을 적용
  const displayName = rawHandle.trim() || handle;
  const locale = resolveLocale(
    undefined,
    typeof navigator === 'undefined' ? undefined : navigator.languages,
  );

  await setDoc(doc(getDb(), 'users', user.uid), {
    handle,
    handleDisplay: rawHandle.trim(),
    displayName,
    ...(user.photoURL ? { photoURL: user.photoURL } : {}),
    locale,
    settings: createDefaultUserSettings(locale, pinSetting),
    createdAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  });
}

async function claimHandle(uid: string, handle: string): Promise<void> {
  const db = getDb();
  const ref = doc(db, 'handles', handle);
  let lastError: unknown;

  for (let attempt = 0; attempt < CLAIM_RETRIES; attempt += 1) {
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (snap.exists()) {
          // 이전 시도에서 내가 이미 선점했다면 성공으로 본다 (재시도 멱등성).
          // users 생성만 실패한 경우 여기로 들어온다.
          if ((snap.data() as { uid?: string }).uid === uid) return;
          throw new HandleTakenError();
        }
        tx.set(ref, { uid, createdAt: serverTimestamp() });
      });
      return;
    } catch (error) {
      if (error instanceof HandleTakenError) throw error;
      lastError = error;
      await delay(CLAIM_BACKOFF_MS * 2 ** attempt);
    }
  }

  throw lastError ?? new Error('handle-claim-failed');
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
